import { styled, Theme } from '@mui/system';

import { useMemo, useState } from 'react';

import { Link } from 'react-router';

import { Typography, LinearProgress, Button } from '@app-velocity/ui';

import { useI18n } from '@app-velocity/i18n';

import { ConfidenceTooltip } from './components/ConfidenceTooltip';
import { ConfidenceStats } from './components/ConfidenceStats';
import { useReportContext } from '../../../../context/useReportContext';
import { ReportPaths } from '../../../../paths/ReportPaths';

const ConfidenceLevelContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  position: 'relative',
}));

const Progress = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontWeight: 700,
  fontSize: 12,
}));

const Header = styled('header')(({ theme }) => ({
  gap: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const Title = styled(Typography)({
  fontWeight: 700,
  flex: 1,
});

const getColorByValue = (value: number, theme: Theme) => {
  if (value >= 100) return theme.palette.success.main;
  if (value >= 5) return theme.palette.warning.main;

  return theme.palette.error.main;
};

const StyledLinearProgress = styled(LinearProgress)<{ totalValue: number }>(
  ({ theme, totalValue }) => ({
    flex: 1,
    backgroundColor: '#e3e3e3',
    borderRadius: theme.spacing(1),
    '.MuiLinearProgress-bar': {
      backgroundColor: getColorByValue(totalValue, theme),
    },
  })
);

export const ConfidenceLevel = () => {
  const { t } = useI18n();
  const { statistics, config, baseURL } = useReportContext();
  const [hovered, setHovered] = useState(false);
  const { hasAccessToSims, humanParticipantsAllowed } = config;
  const { simulatedReviews, uniqueHumanTesters } = statistics;

  const totalSimulations = simulatedReviews.failed + simulatedReviews.succeded;

  const level = useMemo(() => {
    if (!humanParticipantsAllowed) {
      if (totalSimulations >= 30) return 2;
      if (totalSimulations >= 9) return 1;

      return 0;
    }

    if (!uniqueHumanTesters) return 0;
    if (uniqueHumanTesters >= 15) return 3;
    if (uniqueHumanTesters >= 9 && totalSimulations >= 30) return 2;
    if (uniqueHumanTesters >= 5 && totalSimulations >= 9) return 1;

    return 0;
  }, [totalSimulations, uniqueHumanTesters, humanParticipantsAllowed]);

  const handleHover = () => {
    setHovered(true);
  };

  const handleBlur = () => {
    setHovered(false);
  };

  if (humanParticipantsAllowed && typeof uniqueHumanTesters === 'undefined') {
    return null;
  }

  return (
    <ConfidenceLevelContainer
      onMouseEnter={handleHover}
      onMouseLeave={handleBlur}
    >
      {hovered ? <ConfidenceTooltip /> : null}
      <Header>
        <Title>{t('prototypes:report.confidence.title')}</Title>
        <ConfidenceStats
          human={humanParticipantsAllowed ? `${uniqueHumanTesters}` : undefined}
          simulations={`${simulatedReviews.failed + simulatedReviews.succeded}`}
        />
      </Header>
      <Progress>
        <StyledLinearProgress
          variant="determinate"
          max={humanParticipantsAllowed ? 3 : 2}
          totalValue={level}
          value={level}
        />
        <span>{t('prototypes:report.confidence.level', { level })}</span>
      </Progress>
      {hasAccessToSims ? (
        <Button
          fullWidth
          variant="contained"
          color="primary"
          component={Link}
          to={ReportPaths.goToConfidence(baseURL)}
        >
          {t('prototypes:participants.add')}
        </Button>
      ) : null}
    </ConfidenceLevelContainer>
  );
};
